import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Switch,
  Collapse,
} from '@mui/material';
import {
  Science,
  Schedule,
  Add,
  Save,
  QrCodeScanner,
  Warning,
  CheckCircle,
  Timer,
  Inventory,
  Assignment,
  PriorityHigh,
  ContentCopy,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import laboratoryAccessService from '../../services/laboratoryAccessService';

interface DigestionRecord {
  id: string;
  sampleId: string;
  initialWeight: number;
  digestionMethod: string;
  initialVolume: number;
  dilutions: Array<{
    volumeTaken: number;
    finalVolume: number;
    dilutionFactor: number;
  }>;
  totalDilutionFactor: number;
  timestamp: string;
  signature: string;
  status: 'EN_COURS' | 'TERMINE' | 'EN_ATTENTE';
}

const SAAPreparationChimiqueDashboard: React.FC = () => {
  const { user } = useAuth();
  
  // State management
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [preparingBatch, setPreparingBatch] = useState(false); // 🚀 NEW: Button loading state
  const [error, setError] = useState<string | null>(null);
  const [preparationDialog, setPreparationDialog] = useState(false);
  const [batchPreparationDialog, setBatchPreparationDialog] = useState(false);
  const [scanDialog, setScanDialog] = useState(false);
  
  // Form state
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [sampleId, setSampleId] = useState('');
  const [initialWeight, setInitialWeight] = useState<number>(0);
  const [digestionMethod, setDigestionMethod] = useState('');
  const [initialVolume, setInitialVolume] = useState<number>(0);
  const [dilutions, setDilutions] = useState<Array<{volumeTaken: number, finalVolume: number, dilutionFactor: number}>>([]);
  const [newDilutionTaken, setNewDilutionTaken] = useState<number>(0);
  const [newDilutionFinal, setNewDilutionFinal] = useState<number>(0);
  
  // NEW: Additional dilution fields for choice 2
  const [vf1, setVf1] = useState<number>(0);
  const [vp1, setVp1] = useState<number>(0);
  
  // Batch preparation state
  const [batchCommonValues, setBatchCommonValues] = useState({
    digestionMethod: '',
    initialVolume: 0,
    applyToAll: {
      digestionMethod: true,
      initialVolume: true,
    }
  });
  const [batchSampleData, setBatchSampleData] = useState<Record<string, {
    initialWeight: number;
    digestionMethod: string;
    initialVolume: number;
    dilutions: Array<{volumeTaken: number, finalVolume: number, dilutionFactor: number}>;
    volumeTaken: number;
    finalVolume: number;
    calculatedDilutionFactor: number;
    // NEW: Additional dilution fields for choice 2
    vf1: number;
    vp1: number;
  }>>({});
  
  // Dashboard stats
  const [stats, setStats] = useState({
    pending_samples: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
  });
  
  // Reagent alerts - will be loaded from API when available
  const [reagentAlerts, setReagentAlerts] = useState<Array<{name: string, level: number, status: string}>>([]);

  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadSAAPreparationData();
  }, []);

  const loadSAAPreparationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load samples - include multiple statuses for multi-lab workflow support
      // Dynamic limit based on actual total count so large PREPARATION_MECANIQUE backlogs
      // don't starve other statuses from the response window (backend ORDER BY date_creation DESC).
      const statusList = [
        SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE,
        SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE,
        SampleStatus.LABORATOIRE_ICP_ANALYSE,
        SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE,
        SampleStatus.LABORATOIRE_AU_ANALYSE,
        SampleStatus.VALIDATION_SERVICE,
        SampleStatus.PREPARATION_MECANIQUE,
        SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE,
        SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE,
      ];
      const countResponse = await samplesService.getSamples(1, 1, { status: statusList });
      const totalAvailable = countResponse.total || 0;
      const dynamicLimit = Math.max(500, Math.min(totalAvailable, 50000));
      const response = await samplesService.getSamples(1, dynamicLimit, { status: statusList });
      
      // Filter samples that should appear in SAA Prep Dashboard
      const saaPreparationSamples = response.samples.filter(sample => {
        // Regular SAA samples (non-multi-lab)
        if (sample.statut === SampleStatus.LABORATOIRE_SAA_PREP_CHIMIQUE &&
            !sample.metadonnees?.multi_lab_workflow) {
          return true;
        }
        
        // Multi-lab samples with SAA elements that haven't completed SAA prep
        const isMultiLab = sample.metadonnees?.multi_lab_workflow === true;
        if (isMultiLab) {
          const selectedElements = sample.metadonnees?.selected_elements || [];
          const elementRouting = sample.metadonnees?.element_lab_routing || {};
          // 🔧 FIX: Check if ANY selected element routes to SAA (not just elementRouting values)
          const hasSaaElements = selectedElements.some((element: string) => 
            elementRouting[element] === 'LABORATOIRE_SAA'
          );
          // 🔧 FIX: Also check laboratories_involved as fallback
          const hasSaaInLabs = sample.laboratories_involved?.includes('LABORATOIRE_SAA') || false;
          const saaPrepNotDone = sample.metadonnees?.saa_preparation_completed !== true;
          return (hasSaaElements || hasSaaInLabs) && saaPrepNotDone;
        }
        
        return false;
      });
      
      setSamples(saaPreparationSamples);
      
      // Calculate stats from real data
      const urgentSamples = saaPreparationSamples.filter(
        sample => sample.priorite === Priority.URGENTE
      ).length;
      
      const inProgressSamples = saaPreparationSamples.filter(
        sample => sample.metadonnees?.digestion_started
      ).length;
      
      setStats({
        pending_samples: saaPreparationSamples.length,
        in_progress: inProgressSamples,
        completed_today: 0, // TODO: Calculate from actual completion dates
        urgent_samples: urgentSamples,
      });
      
      // TODO: Load reagent stock from API when available
      // For now, show empty state
      setReagentAlerts([]);
      
    } catch (error) {
      console.error('Error loading SAA preparation data:', error);
      setError('Aucun échantillon en préparation chimique SAA trouvé. Vérifiez que des échantillons ont été assignés au laboratoire SAA.');
    } finally {
      setLoading(false);
    }
  };

  const digestionMethods = [
    { value: 'HCl', label: 'HCl (pour carbonates)', description: 'Dissolution des carbonates' },
    { value: 'HNO3', label: 'HNO3 (pour sulfures)', description: 'Dissolution des sulfures' },
    { value: 'AQUA_REGIA', label: 'Eau régale (HCl + HNO3)', description: 'Dissolution métaux nobles' },
    { value: 'FOUR_ACID', label: '4 acides (HF + HClO4 + HNO3 + HCl)', description: 'Digestion complète' },
  ];

  const calculateDilutionFactor = (volumeTaken: number, finalVolume: number): number => {
    if (volumeTaken === 0) return 0;
    return finalVolume / volumeTaken;
  };

  const calculateTotalDilutionFactor = (): number => {
    return dilutions.reduce((total, dilution) => total * dilution.dilutionFactor, 1);
  };

  const addDilution = () => {
    if (newDilutionTaken > 0 && newDilutionFinal > 0) {
      const dilutionFactor = calculateDilutionFactor(newDilutionTaken, newDilutionFinal);
      setDilutions([...dilutions, {
        volumeTaken: newDilutionTaken,
        finalVolume: newDilutionFinal,
        dilutionFactor: dilutionFactor
      }]);
      setNewDilutionTaken(0);
      setNewDilutionFinal(0);
    }
  };

  const handleSavePreparation = async () => {
    // Determine the actual UUID to use for API calls
    let actualSampleId: string;
    
    if (selectedSample) {
      // Use the UUID from the selected sample
      actualSampleId = selectedSample.id;
    } else {
      // If manually entered, try to find the sample by sample_id
      const foundSample = samples.find(s => s.sample_id === sampleId);
      if (!foundSample) {
        setError('Échantillon non trouvé. Veuillez sélectionner un échantillon de la liste.');
        return;
      }
      actualSampleId = foundSample.id;
    }

    if (!sampleId || initialWeight === 0 || !digestionMethod || initialVolume === 0) {
      setError('Tous les champs obligatoires doivent être remplis');
      return;
    }

    try {
      const preparationData = {
        sampleId,
        initialWeight,
        digestionMethod,
        initialVolume,
        dilutions,
        totalDilutionFactor: calculateTotalDilutionFactor(),
        // NEW: Additional dilution fields for choice 2
        vf1: vf1,
        vp1: vp1,
        timestamp: new Date().toISOString(),
        signature: `${user?.firstName} ${user?.lastName}`,
        technician_id: user?.id,
      };

      // Save preparation data and move to analysis status
      await samplesService.updateSample(actualSampleId, {
        metadonnees: {
          saa_preparation: preparationData,
          digestion_started: true,
          digestion_method: digestionMethod,
          preparation_completed_at: new Date().toISOString(),
        }
      });

      // Move sample to SAA analysis status
      await samplesService.updateSampleStatus(
        actualSampleId,
        SampleStatus.LABORATOIRE_SAA_ANALYSE,
        `Préparation SAA terminée - Méthode: ${digestionMethod}, Poids initial: ${initialWeight}g, Volume initial: ${initialVolume}mL, Facteur dilution total: ${calculateTotalDilutionFactor()}`
      );

      setPreparationDialog(false);
      resetForm();
      loadSAAPreparationData();
      
    } catch (error) {
      console.error('Error saving preparation:', error);
      setError('Erreur lors de la sauvegarde de la préparation');
    }
  };

  const resetForm = () => {
    setSelectedSample(null);
    setSampleId('');
    setInitialWeight(0);
    setDigestionMethod('');
    setInitialVolume(0);
    setDilutions([]);
    setNewDilutionTaken(0);
    setNewDilutionFinal(0);
    // NEW: Reset additional dilution fields
    setVf1(0);
    setVp1(0);
    setError(null);
  };

  const getReagentStatusColor = (status: string) => {
    switch (status) {
      case 'CRITIQUE': return 'error';
      case 'FAIBLE': return 'warning';
      case 'OK': return 'success';
      default: return 'inherit';
    }
  };

  // Helper function to group samples by batch
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  // Check if sample is part of a batch
  const isBatchSample = (sample: Sample): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true;
  };

  // Get batch number for sample
  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  // Get batch position for sample
  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  // Get total samples in batch
  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  // Initialize batch sample data
  const initializeBatchSampleData = (samples: Sample[]) => {
    const initialData: Record<string, any> = {};
    samples.forEach(sample => {
      // 🔬 SCALE INTEGRATION: Use scale weight if available, otherwise default 0.5
      const scaleWeight = sample.metadonnees?.scale_weights?.saa?.weight_grams;
      const hasScaleWeight = scaleWeight !== undefined && scaleWeight !== null;
      
      initialData[sample.id] = {
        initialWeight: hasScaleWeight ? scaleWeight : 0.5, // Use scale weight or default
        hasScaleWeight: hasScaleWeight, // Flag to show indicator in UI
        scaleRecordedAt: sample.metadonnees?.scale_weights?.saa?.recorded_at,
        digestionMethod: batchCommonValues.digestionMethod,
        initialVolume: batchCommonValues.initialVolume,
        dilutions: [],
        volumeTaken: 0,
        finalVolume: 0,
        calculatedDilutionFactor: 1,
        // NEW: Additional dilution fields for choice 2
        vf1: 0,
        vp1: 0,
      };
    });
    setBatchSampleData(initialData);
  };

  // Apply common values to all batch samples
  const applyCommonValuesToBatch = () => {
    const updatedData = { ...batchSampleData };
    
    if (selectedBatch) {
      selectedBatch.samples.forEach(sample => {
        // Ensure sample data exists with all required fields
        if (!updatedData[sample.id]) {
          updatedData[sample.id] = {
            initialWeight: 0,
            digestionMethod: '',
            initialVolume: 0,
            dilutions: [],
            volumeTaken: 0,
            finalVolume: 0,
            calculatedDilutionFactor: 1,
            vf1: 0,
            vp1: 0,
          };
        }
        
        if (batchCommonValues.applyToAll.digestionMethod) {
          updatedData[sample.id].digestionMethod = batchCommonValues.digestionMethod;
        }
        if (batchCommonValues.applyToAll.initialVolume) {
          updatedData[sample.id].initialVolume = batchCommonValues.initialVolume;
        }
      });
    }
    
    setBatchSampleData(updatedData);
  };

  // Update individual sample data in batch with auto-calculation
  const updateBatchSampleData = (sampleId: string, field: string, value: any) => {
    setBatchSampleData(prev => {
      const currentData = prev[sampleId] || {
        initialWeight: 0,
        digestionMethod: '',
        initialVolume: 0,
        dilutions: [],
        volumeTaken: 0,
        finalVolume: 0,
        calculatedDilutionFactor: 1,
        vf1: 0,
        vp1: 0,
      };
      const updatedData = {
        ...currentData,
        [field]: value
      };
      
      // Auto-calculate dilution factor when volume fields change
      if (field === 'volumeTaken' || field === 'finalVolume') {
        const volumeTaken = field === 'volumeTaken' ? value : currentData.volumeTaken || 0;
        const finalVolume = field === 'finalVolume' ? value : currentData.finalVolume || 0;
        
        if (volumeTaken > 0 && finalVolume > 0) {
          updatedData.calculatedDilutionFactor = finalVolume / volumeTaken;
        } else {
          updatedData.calculatedDilutionFactor = 1;
        }
      }
      
      return {
        ...prev,
        [sampleId]: updatedData
      };
    });
  };

  // Duplicate a specific value to all samples in the batch
  const duplicateValueToAll = (field: string, value: any, sourceSampleId: string) => {
    if (!selectedBatch) return;
    
    const updatedData = { ...batchSampleData };
    selectedBatch.samples.forEach(sample => {
      if (sample.id !== sourceSampleId) { // Don't update the source sample
        const currentData = updatedData[sample.id] || {
          initialWeight: 0,
          digestionMethod: '',
          initialVolume: 0,
          dilutions: [],
          volumeTaken: 0,
          finalVolume: 0,
          calculatedDilutionFactor: 1,
          vf1: 0,
          vp1: 0,
        };
        updatedData[sample.id] = {
          ...currentData,
          [field]: value
        };
        
        // Recalculate dilution factor if volume fields are duplicated
        if (field === 'volumeTaken' || field === 'finalVolume') {
          const volumeTaken = field === 'volumeTaken' ? value : currentData.volumeTaken || 0;
          const finalVolume = field === 'finalVolume' ? value : currentData.finalVolume || 0;
          
          if (volumeTaken > 0 && finalVolume > 0) {
            updatedData[sample.id].calculatedDilutionFactor = finalVolume / volumeTaken;
          } else {
            updatedData[sample.id].calculatedDilutionFactor = 1;
          }
        }
      }
    });
    
    setBatchSampleData(updatedData);
  };

  // Save batch preparation and move all samples to SAA analysis
  const handleSaveBatchPreparation = async () => {
    if (!selectedBatch) return;

    try {
      setLoading(true);
      
      // Validate all samples have required data
      const invalidSamples = selectedBatch.samples.filter(sample => {
        const sampleData = batchSampleData[sample.id];
        return !sampleData?.initialWeight || !sampleData?.digestionMethod || !sampleData?.initialVolume;
      });

      if (invalidSamples.length > 0) {
        throw new Error(`${invalidSamples.length} échantillon(s) incomplet(s) - tous les champs sont requis`);
      }

      // 🚀 PERFORMANCE OPTIMIZATION: Use bulk operations instead of individual calls
      console.log(`🔄 Using bulk preparation save for ${selectedBatch.samples.length} samples...`);
      
      // 🚀 PERFORMANCE OPTIMIZATION: Use bulk API for batch operations
      console.log(`🔄 Using bulk preparation save for ${selectedBatch.samples.length} samples...`);
      
      // Prepare bulk update data with MULTI-LAB AWARENESS
      const bulkUpdateData = selectedBatch.samples.map(sample => {
        const sampleData = batchSampleData[sample.id];
        
        const preparationData = {
          sampleId: sample.sample_id,
          initialWeight: sampleData.initialWeight,
          digestionMethod: sampleData.digestionMethod,
          initialVolume: sampleData.initialVolume,
          dilutions: sampleData.volumeTaken && sampleData.finalVolume ? [{
            volumeTaken: sampleData.volumeTaken,
            finalVolume: sampleData.finalVolume,
            dilutionFactor: sampleData.calculatedDilutionFactor
          }] : [],
          totalDilutionFactor: sampleData.calculatedDilutionFactor || 1,
          vf1: sampleData.vf1 || 0,
          vp1: sampleData.vp1 || 0,
          timestamp: new Date().toISOString(),
          signature: `${user?.firstName} ${user?.lastName}`,
          technician_id: user?.id,
          batch_preparation: true,
          batch_number: selectedBatch.batchNumber,
        };

        // 🔥 MULTI-LAB FIX: For multi-lab samples, check if sample is already in a protected status
        // to avoid overwriting status from another lab's workflow
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                  sample.metadonnees?.bgm_method_selected === true;
        const currentStatus = sample.statut;
        const protectedStatuses = [
          SampleStatus.VALIDATION_SERVICE,
          SampleStatus.LABORATOIRE_ICP_ANALYSE,
          SampleStatus.LABORATOIRE_AU_ANALYSE,
          SampleStatus.GENERATION_CERTIFICAT
        ];
        
        // Determine target status
        let newStatus = SampleStatus.LABORATOIRE_SAA_ANALYSE;
        if (isMultiLabSample && protectedStatuses.includes(currentStatus as SampleStatus)) {
          // Keep current status to avoid overwriting another lab's workflow
          newStatus = currentStatus as SampleStatus;
          console.log(`🔬 Multi-lab sample ${sample.sample_id}: preserving status ${currentStatus} instead of SAA_ANALYSE`);
        }

        return {
          id: sample.id,
          sample_id: sample.sample_id,
          metadonnees: {
            ...sample.metadonnees,
            saa_preparation: preparationData,
            saa_preparation_completed: true,  // 🔥 Critical flag for multi-lab workflow
            digestion_started: true,
            digestion_method: sampleData.digestionMethod,
            preparation_completed_at: new Date().toISOString(),
          },
          status: newStatus,
          status_comment: `Préparation SAA LOT terminée - Méthode: ${sampleData.digestionMethod}, Poids: ${sampleData.initialWeight}g, Volume: ${sampleData.initialVolume}mL`
        };
      });

      // Check if we have mixed statuses (multi-lab scenario)
      const uniqueStatuses = [...new Set(bulkUpdateData.map(item => item.status))];
      const hasMultiLabSamples = uniqueStatuses.length > 1;
      
      let result;
      if (hasMultiLabSamples) {
        // 🔥 Multi-lab: Update samples individually to respect per-sample status
        console.log(`📝 Multi-lab batch detected - updating ${bulkUpdateData.length} samples individually...`);
        const updatePromises = bulkUpdateData.map(async (updateData) => {
          try {
            await samplesService.updateSample(updateData.id, {
              metadonnees: updateData.metadonnees,
              statut: updateData.status,
              commentaires: updateData.status_comment
            });
            return { success: true, id: updateData.id };
          } catch (error: any) {
            return { success: false, id: updateData.id, error: error.message };
          }
        });
        const results = await Promise.all(updatePromises);
        result = {
          updated: results.filter(r => r.success).length,
          errors: results.filter(r => !r.success).map(r => `${r.id}: ${r.error}`)
        };
      } else {
        // Use bulk update API for regular samples (all same status)
        const sampleIds = bulkUpdateData.map(item => item.id);
        const metadataUpdates = Object.fromEntries(
          bulkUpdateData.map(item => [item.id, item.metadonnees])
        );
        const statusUpdate = {
          status: bulkUpdateData[0].status,  // All same status
          comment: 'Préparation SAA LOT terminée'
        };
        result = await samplesService.updateSampleBatch(sampleIds, metadataUpdates, statusUpdate);
      }

      if (result.errors.length > 0) {
        console.warn(`⚠️ Some errors during bulk save:`, result.errors);
        // Still continue if most samples were successful
      }

      console.log(`✅ Batch ${selectedBatch.batchNumber} preparation completed - ${result.updated} samples moved to SAA analysis`);
      
      setBatchPreparationDialog(false);
      setSelectedBatch(null);
      setBatchSampleData({});
      loadSAAPreparationData(); // Refresh the dashboard
      
    } catch (error) {
      console.error('❌ Error saving batch preparation:', error);
      setError(`Erreur lors de la sauvegarde du lot: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            ⚗️ Technicien Préparation Chimique SAA
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Digestion & Préparation pour Absorption Atomique - {user?.firstName} {user?.lastName}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setPreparationDialog(true)}
        >
          Nouvelle Préparation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    En Attente
                  </Typography>
                  <Typography variant="h4">
                    {stats.pending_samples}
                  </Typography>
                </Box>
                <Assignment color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    En Cours
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.in_progress}
                  </Typography>
                </Box>
                <Timer color="warning" />
              </Box>
        </CardContent>
      </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Urgents
                  </Typography>
                  <Typography variant="h4" color="error">
                    {stats.urgent_samples}
                  </Typography>
                </Box>
                <PriorityHigh color="error" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Terminés Aujourd'hui
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.completed_today}
                  </Typography>
                </Box>
                <CheckCircle color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Samples Queue */}
        <Grid item xs={12} md={8}>
      <Card>
        <CardContent>
              <Typography variant="h6" gutterBottom>
                Échantillons en Attente de Préparation
          </Typography>
          
          {samples.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Science sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Aucun échantillon en attente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aucun échantillon n'est actuellement assigné pour la préparation chimique SAA.
                    <br />
                    Contactez la réception pour vérifier les échantillons entrants.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID Échantillon / Lot</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Priorité</TableCell>
                        <TableCell>Méthodes</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Group samples by batch
                        const { batches, individualSamples } = groupSamplesByBatch(samples);
                        const items: Array<{type: 'batch', batchNumber: string, samples: Sample[]} | {type: 'individual', sample: Sample}> = [];
                        
                        // Add batch items
                        Object.entries(batches).forEach(([batchNumber, batchSamples]) => {
                          items.push({ type: 'batch', batchNumber, samples: batchSamples });
                        });
                        
                        // Add individual items
                        individualSamples.forEach(sample => {
                          items.push({ type: 'individual', sample });
                        });
                        
                        return items.map((item, index) => {
                          if (item.type === 'batch') {
                            const firstSample = item.samples[0];
                            const totalWeight = item.samples.reduce((sum, s) => sum + (s.metadonnees?.poids_kg || 0), 0);
                            const isExpanded = expandedBatches.has(item.batchNumber);
                            
                            return (
                              <React.Fragment key={`batch-${item.batchNumber}`}>
                                {/* Batch Header Row - Now Clickable */}
                                <TableRow 
                                  sx={{ 
                                  backgroundColor: '#e8f5e8', 
                                  '&:hover': { backgroundColor: '#c8e6c9' },
                                    borderLeft: '4px solid #4caf50',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => toggleBatchExpansion(item.batchNumber)}
                                >
                                  <TableCell colSpan={5}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      <IconButton size="small" sx={{ p: 0 }}>
                                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                      <Box sx={{ 
                                        backgroundColor: '#4caf50', 
                                        color: 'white', 
                                        px: 2, 
                                        py: 0.5, 
                                        borderRadius: 1,
                                        fontWeight: 'bold'
                                      }}>
                                        LOT SAA
                                      </Box>
                                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                        {item.batchNumber}
                                      </Typography>
                                      <Chip 
                                        label={`${item.samples.length} échantillons`} 
                                        size="small" 
                                        color="success" 
                                        variant="outlined"
                                      />
                                      <Chip 
                                        label={`${totalWeight.toFixed(2)} kg total`} 
                                        size="small" 
                                        color="info" 
                                        variant="outlined"
                                      />
                                      <Typography variant="body2" color="text.secondary">
                                        Client: {firstSample.client?.company_name || 'N/A'}
                                      </Typography>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        color="success"
                                        startIcon={preparingBatch ? <CircularProgress size={16} /> : <Science />}
                                        disabled={preparingBatch} // 🚀 Disable during loading
                                        onClick={async (e) => {
                                          e.stopPropagation(); // Prevent batch expansion when clicking button
                                          setPreparingBatch(true); // 🚀 Show loading spinner
                                          
                                          // Allow React to re-render and show the spinner
                                          await new Promise(resolve => setTimeout(resolve, 50));
                                          
                                          setSelectedBatch({ batchNumber: item.batchNumber, samples: item.samples });
                                          initializeBatchSampleData(item.samples);
                                          setBatchPreparationDialog(true);
                                          setPreparingBatch(false); // 🚀 Hide loading spinner
                                        }}
                                        sx={{ ml: 'auto' }}
                                      >
                                        Préparer le Lot
                                      </Button>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                                
                                {/* Individual Sample Rows - Only show when expanded */}
                                {isExpanded && item.samples.map((sample, sampleIndex) => (
                                  <TableRow 
                                    key={sample.id}
                                    sx={{ 
                                      backgroundColor: '#f1f8e9',
                                      borderLeft: '2px solid #4caf50'
                                    }}
                                  >
                                    <TableCell sx={{ pl: 4 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" fontWeight="bold">
                                          {sample.sample_id}
                                        </Typography>
                                        <Chip 
                                          label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem' }}
                                        />
                                      </Box>
                                    </TableCell>
                                    <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={sample.priorite}
                                        size="small"
                                        color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {sample.metadonnees?.analyses_demandees?.filter((a: string) => a.includes('AA')).join(', ') || 'AA Standard'}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Science />}
                                        onClick={() => {
                                          setSelectedSample(sample);
                                          setSampleId(sample.sample_id);
                                          setPreparationDialog(true);
                                        }}
                                      >
                                        Préparer Individuel
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            );
                          } else {
                            // Individual sample
                            const sample = item.sample;
                            return (
                              <TableRow key={sample.id}>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {sample.sample_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={sample.priorite}
                                    size="small"
                                    color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>
                                  {sample.metadonnees?.analyses_demandees?.filter((a: string) => a.includes('AA')).join(', ') || 'AA Standard'}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Science />}
                                    onClick={() => {
                                      setSelectedSample(sample);
                                      setSampleId(sample.sample_id);
                                      setPreparationDialog(true);
                                    }}
                                  >
                                    Préparer
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          }
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Reagent Alerts */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory />
                Alertes Stock Réactifs
              </Typography>
              
            <List>
                {reagentAlerts.length > 0 ? (
                  reagentAlerts.map((reagent, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                        <Warning color={getReagentStatusColor(reagent.status) as any} />
                  </ListItemIcon>
                  <ListItemText
                        primary={reagent.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Niveau: {reagent.level}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={reagent.level}
                              color={getReagentStatusColor(reagent.status) as any}
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        }
                      />
                      <Chip
                        label={reagent.status}
                        size="small"
                        color={getReagentStatusColor(reagent.status) as any}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Aucune alerte de stock"
                      secondary="Le système de gestion des réactifs sera bientôt disponible"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preparation Form Dialog */}
      <Dialog open={preparationDialog} onClose={() => setPreparationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Science color="primary" />
            <Typography variant="h6">
              Formulaire de Préparation Chimique SAA
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Sample ID with Barcode Scanner */}
            <Box display="flex" gap={2} mb={3}>
              <TextField
                fullWidth
                label="ID Échantillon *"
                value={sampleId}
                onChange={(e) => {
                  setSampleId(e.target.value);
                  setSelectedSample(null); // Clear selection when manually typing
                }}
                placeholder="Saisir ou scanner le code-barres"
                helperText={selectedSample ? `Échantillon sélectionné: ${selectedSample.client?.company_name}` : "Saisissez l'ID ou sélectionnez depuis la liste"}
              />
              <IconButton
                color="primary"
                onClick={() => setScanDialog(true)}
                sx={{ border: 1, borderColor: 'primary.main' }}
              >
                <QrCodeScanner />
              </IconButton>
            </Box>

            {/* Initial Weight */}
            <TextField
              fullWidth
              type="number"
              label="Poids initial échantillon (g) *"
              value={initialWeight}
              onChange={(e) => setInitialWeight(Number(e.target.value))}
              sx={{ mb: 3 }}
              inputProps={{ min: 0, step: 0.01 }}
            />

            {/* Digestion Method */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Méthode de digestion *</InputLabel>
              <Select
                value={digestionMethod}
                onChange={(e) => setDigestionMethod(e.target.value)}
              >
                {digestionMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>
                    <Box>
                      <Typography variant="body1">{method.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {method.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Initial Volume */}
            <TextField
              fullWidth
              type="number"
              label="Volume initial après digestion (mL) *"
              value={initialVolume}
              onChange={(e) => setInitialVolume(Number(e.target.value))}
              sx={{ mb: 3 }}
              inputProps={{ min: 0, step: 0.1 }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Dilutions Section */}
            <Typography variant="h6" gutterBottom>
              Dilutions
            </Typography>
            
            <Box display="flex" gap={2} mb={2}>
              <TextField
                type="number"
                label="Volume prélevé (mL)"
                value={newDilutionTaken}
                onChange={(e) => setNewDilutionTaken(Number(e.target.value))}
                inputProps={{ min: 0, step: 0.1 }}
              />
              <TextField
                type="number"
                label="Volume final (mL)"
                value={newDilutionFinal}
                onChange={(e) => setNewDilutionFinal(Number(e.target.value))}
                inputProps={{ min: 0, step: 0.1 }}
              />
              <Button
                variant="outlined"
                onClick={addDilution}
                disabled={newDilutionTaken === 0 || newDilutionFinal === 0}
              >
                Ajouter
              </Button>
            </Box>

            {/* Dilutions Table */}
            {dilutions.length > 0 && (
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Volume Prélevé (mL)</TableCell>
                      <TableCell>Volume Final (mL)</TableCell>
                      <TableCell>Facteur de Dilution</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dilutions.map((dilution, index) => (
                      <TableRow key={index}>
                        <TableCell>{dilution.volumeTaken}</TableCell>
                        <TableCell>{dilution.finalVolume}</TableCell>
                        <TableCell>{dilution.dilutionFactor.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Facteur de Dilution Total:
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary">
                          {calculateTotalDilutionFactor().toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 3 }} />

            {/* NEW: Additional Dilution Fields for Choice 2 */}
            <Typography variant="h6" gutterBottom>
              Variables Supplémentaires (pour choix dilution 2)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ces champs seront utilisés par l'analyste pour le choix de dilution 2: L × (V/p) × (vf1/vp1)
            </Typography>
            
            <Box display="flex" gap={2} mb={3}>
              <TextField
                type="number"
                label="Volume final alternatif (vf1) en mL"
                value={vf1}
                onChange={(e) => setVf1(Number(e.target.value))}
                inputProps={{ min: 0, step: 0.1 }}
                helperText="Volume final pour formule alternative"
              />
              <TextField
                type="number"
                label="Volume prélevé alternatif (vp1) en mL"
                value={vp1}
                onChange={(e) => setVp1(Number(e.target.value))}
                inputProps={{ min: 0, step: 0.1 }}
                helperText="Volume prélevé pour formule alternative"
              />
            </Box>

            {vf1 > 0 && vp1 > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Facteur de dilution alternatif (vf1/vp1):</strong> {(vf1 / vp1).toFixed(2)}
                </Typography>
              </Alert>
            )}

            {/* Signature */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Signature électronique: {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date/Heure: {new Date().toLocaleString('fr-FR')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreparationDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSavePreparation}
            variant="contained"
            startIcon={<Save />}
            disabled={!sampleId || initialWeight === 0 || !digestionMethod || initialVolume === 0}
          >
            Sauvegarder Préparation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Batch Preparation Dialog - Optimized for Large Batches */}
      <Dialog 
        open={batchPreparationDialog} 
        onClose={() => {
          setBatchPreparationDialog(false);
        }} 
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#2e7d32',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1000
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
              <Science sx={{ fontSize: '1.8rem' }} />
              <Typography variant="h5" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                🧪 Préparation Chimique SAA - Lot {selectedBatch?.batchNumber}
            </Typography>
            <Chip 
              label={`${selectedBatch?.samples.length || 0} échantillons`} 
              color="success" 
              variant="outlined" 
                sx={{ 
                  color: 'white', 
                  borderColor: 'white',
                  fontSize: '0.75rem'
                }}
            />
          </Box>
            
            {/* Progress Indicator */}
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                Prêts: {selectedBatch?.samples?.filter(sample => {
                  const sampleData = batchSampleData[sample.id];
                  return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
                }).length || 0}/{selectedBatch?.samples?.length || 0}
                              </Typography>
                            </Box>
                    </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ mt: 1 }}>

            {/* Batch Samples Table - Optimized for Large Batches */}
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    📋 Saisie des Préparations - Lot {selectedBatch?.batchNumber}
                </Typography>
                
                  {/* Methods Display */}
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {digestionMethods.slice(0, 3).map((method) => (
                      <Chip
                        key={method.value}
                        label={method.value}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '20px' }}
                      />
                    ))}
                  </Box>
                </Box>
                
                <TableContainer sx={{ 
                  maxHeight: 'calc(100vh - 300px)', 
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ 
                        backgroundColor: '#e8f5e8',
                        '& .MuiTableCell-head': {
                          backgroundColor: '#2e7d32',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          padding: '6px 8px',
                          whiteSpace: 'nowrap'
                        }
                      }}>
                        <TableCell>Échantillon</TableCell>
                        <TableCell>Poids (g)</TableCell>
                        <TableCell>Méthode</TableCell>
                        <TableCell>Vol Initial (mL)</TableCell>
                        <TableCell>Vol Prélevé (mL)</TableCell>
                        <TableCell>Vol Final (mL)</TableCell>
                        <TableCell>Facteur</TableCell>
                        {/* NEW: Additional dilution columns for choice 2 */}
                        <TableCell>vf1 (mL)</TableCell>
                        <TableCell>vp1 (mL)</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBatch?.samples
                        .sort((a, b) => {
                          // Smart numeric sorting
                          const aNum = parseInt(a.sample_id.replace(/\D/g, ''));
                          const bNum = parseInt(b.sample_id.replace(/\D/g, ''));
                          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                          return a.sample_id.localeCompare(b.sample_id);
                        })
                        .map((sample, index) => {
                        const sampleData = batchSampleData[sample.id] || {
                          initialWeight: 0,
                          digestionMethod: '',
                          initialVolume: 0,
                          dilutions: [],
                          volumeTaken: 0,
                          finalVolume: 0,
                          calculatedDilutionFactor: 1,
                          vf1: 0,
                          vp1: 0,
                        };
                        
                        return (
                          <TableRow 
                            key={sample.id}
                            sx={{ 
                              backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                              '&:hover': { backgroundColor: '#f0f8ff' },
                              '& .MuiTableCell-root': {
                                padding: '4px 6px',
                                fontSize: '0.75rem'
                              }
                            }}
                          >
                            {/* Sample ID - Compact */}
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                                  {sample.sample_id}
                                </Typography>
                            </TableCell>
                            
                            {/* Weight - Compact */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.initialWeight || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'initialWeight', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.01 }}
                                  sx={{ 
                                    width: 60,
                                    // 🔬 SCALE: Green border if weight came from scale
                                    '& .MuiOutlinedInput-root': sampleData.hasScaleWeight ? {
                                      '& fieldset': { borderColor: 'success.main', borderWidth: 2 }
                                    } : {}
                                  }}
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                {/* 🔬 SCALE INDICATOR: Show badge if weight came from scale */}
                                {sampleData.hasScaleWeight && (
                                  <Chip
                                    label="⚖️"
                                    size="small"
                                    color="success"
                                    title={`Poids de la balance - ${new Date(sampleData.scaleRecordedAt).toLocaleString()}`}
                                    sx={{ 
                                      height: 18, 
                                      fontSize: '0.6rem',
                                      '& .MuiChip-label': { px: 0.5 }
                                    }}
                                  />
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('initialWeight', sampleData.initialWeight, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Method - Compact */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <FormControl size="small" sx={{ minWidth: 80 }}>
                                  <Select
                                    value={sampleData.digestionMethod || ''}
                                    onChange={(e) => updateBatchSampleData(sample.id, 'digestionMethod', e.target.value)}
                                    displayEmpty
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: '28px',
                                      '& .MuiSelect-select': { py: 0.5 }
                                    }}
                                    renderValue={(selected) => {
                                      if (!selected) return <em style={{fontSize: '0.65rem', color: '#999'}}>Méthode</em>;
                                      return selected;
                                    }}
                                  >
                                    {digestionMethods.map(method => (
                                      <MenuItem key={method.value} value={method.value}>
                                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>{method.value}</Typography>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('digestionMethod', sampleData.digestionMethod, sample.id)}
                                  title="Copier vers tous"
                                  disabled={!sampleData.digestionMethod}
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Initial Volume - Compact */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.initialVolume || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'initialVolume', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('initialVolume', sampleData.initialVolume, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Volume Prélevé - Compact */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.volumeTaken || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'volumeTaken', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  placeholder="0.0"
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('volumeTaken', sampleData.volumeTaken, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Volume Final - Compact */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.finalVolume || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'finalVolume', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  placeholder="0.0"
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('finalVolume', sampleData.finalVolume, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Calculated Dilution Factor - Compact */}
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color={sampleData.calculatedDilutionFactor > 1 ? 'primary' : 'text.secondary'}
                                fontWeight={sampleData.calculatedDilutionFactor > 1 ? 'bold' : 'normal'}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {sampleData.calculatedDilutionFactor ? sampleData.calculatedDilutionFactor.toFixed(2) : '1.00'}x
                              </Typography>
                            </TableCell>
                            
                            {/* NEW: vf1 Field - Compact */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.vf1 || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'vf1', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('vf1', sampleData.vf1, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* NEW: vp1 Field - Compact */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={sampleData.vp1 || 0}
                                  onChange={(e) => updateBatchSampleData(sample.id, 'vp1', Number(e.target.value))}
                                  inputProps={{ min: 0, step: 0.1 }}
                                  sx={{ width: 60 }}
                                  InputProps={{
                                    sx: { fontSize: '0.7rem', height: '28px' }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => duplicateValueToAll('vp1', sampleData.vp1, sample.id)}
                                  title="Copier vers tous"
                                  sx={{ 
                                    p: 0.2, 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <ContentCopy sx={{ fontSize: 10 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            
                            {/* Status - Compact */}
                            <TableCell>
                              <Chip
                                label={sampleData.initialWeight && sampleData.digestionMethod && sampleData.initialVolume ? 'OK' : 'Manque'}
                                size="small"
                                color={sampleData.initialWeight && sampleData.digestionMethod && sampleData.initialVolume ? 'success' : 'warning'}
                                sx={{ 
                                  fontSize: '0.6rem', 
                                  height: '18px',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Compact Summary Footer */}
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  backgroundColor: '#e8f5e8', 
                  borderRadius: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '0.85rem' }}>
                    📊 Résumé: {selectedBatch?.samples.length || 0} échantillons
                  </Typography>
                  
                  <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Poids Total</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.reduce((sum, sample) => {
                          const sampleData = batchSampleData[sample.id];
                          return sum + (sampleData?.initialWeight || 0);
                        }, 0).toFixed(2)} g
                      </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Prêts</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.filter(sample => {
                          const sampleData = batchSampleData[sample.id];
                          return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
                        }).length || 0}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Volume Total</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                        {selectedBatch?.samples.reduce((sum, sample) => {
                          const sampleData = batchSampleData[sample.id];
                          return sum + (sampleData?.initialVolume || 0);
                        }, 0).toFixed(1)} mL
                      </Typography>
                    </Box>
                  </Box>
                </Box>
        </CardContent>
      </Card>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000
        }}>
          <Button 
            onClick={() => setBatchPreparationDialog(false)}
            sx={{ fontSize: '0.8rem' }}
          >
            Annuler
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleSaveBatchPreparation}
            disabled={!selectedBatch?.samples.every(sample => {
              const sampleData = batchSampleData[sample.id];
              return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
            })}
            sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
          >
            💾 Sauvegarder le Lot ({selectedBatch?.samples?.filter(sample => {
              const sampleData = batchSampleData[sample.id];
              return sampleData?.initialWeight && sampleData?.digestionMethod && sampleData?.initialVolume;
            }).length || 0}/{selectedBatch?.samples?.length || 0})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SAAPreparationChimiqueDashboard; 